import { ConfigService, LocaleService } from '@assist/shared/data';
import { coreConfig } from '@assist/shared/core';
import { importProvidersFrom, inject, Injectable, isDevMode, Type } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from './environments/environment';
import { ASSIST_OPEN_NEW_RESERVATION_DIALOG } from '@assist/ui';
import { NewReservationDialogComponent } from '@assist/reservation-detail';
import { TranslocoService } from '@jsverse/transloco';
import { provideCueAnalytics, withHeadersIntecceptor } from '@cue/analytics';
import { provideNavigationSettings } from '@cue/navigation-settings';
import { provideLocalization } from '@cue/translate';
import { localizations } from '@cue/translations';
import { CALENDAR_SYNC_API_BASE_URL } from '@cue/calendar-sync';
import { CALENDARS_CONFIGURATION, CalendarsService } from '@cue/calendars';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { provideEffects } from '@ngrx/effects';
import { NotificationsEffects } from '@assist/notifications/services';
import { MessageService } from '@progress/kendo-angular-l10n';
import { RtlProvider } from '@cue/utilities';
import { provideDeclarativeMapImplementationTypeResolver } from '@cue/map/core';
import { LeafletDeclarativeMapImplementation } from '@cue/map/leaflet';
import { OpenLayersDeclarativeMapImplementation } from '@cue/map/openlayers';

@Injectable()
export class KendoIntlService extends CldrIntlService {
  private readonly localeService = inject(LocaleService);

  override firstDay(): number {
    const intlFirstDay = this.localeService.getFirstDayOfWeek();
    return intlFirstDay === 'monday' ? 1 : 0;
  }
}

export const notEnabledConfig = (configService: ConfigService) => {
  return {
    providers: [
      importProvidersFrom(
        ServiceWorkerModule.register('/ngsw-worker.js', {
          enabled: environment.production,
          registrationStrategy: 'registerImmediately',
        })
      ),
    ],
  };
};

export const appConfig = (configService: ConfigService, calendarService: Type<CalendarsService>) => {
  return {
    providers: [
      {
        provide: RtlProvider,
        useExisting: LocaleService,
      },
      MessageService,
      provideEffects([NotificationsEffects]),
      {
        provide: IntlService,
        useClass: KendoIntlService,
      },
      {
        useFactory: (configService: ConfigService) => ({
          cachingEnabled: configService.value.cachingEnabled,
          ewsApiUrl: configService.value.apiURL,
          calendarApiUrl: configService.value.calendarURL,
        }),
        deps: [ConfigService],
        provide: CALENDARS_CONFIGURATION,
      },
      {
        provide: CalendarsService,
        useClass: calendarService,
      },
      {
        provide: CALENDAR_SYNC_API_BASE_URL,
        useValue: `${configService.value.apiURL}/api/calendarsync`,
      },
      {
        provide: ASSIST_OPEN_NEW_RESERVATION_DIALOG,
        useFactory: () => {
          const dialog = inject(Dialog);
          return () =>
            dialog.open(NewReservationDialogComponent, {
              panelClass: ['full-height-modal', 'new-reservation-dialog-panel'],
              data: {
                mode: 'new',
                closeOnSave: true,
              },
            });
        },
      },
      coreConfig(configService).providers,
      provideDeclarativeMapImplementationTypeResolver(() => {
        const configService = inject(ConfigService);
        // return OpenLayersDeclarativeMapImplementation;
        return configService.value.mapEngine === 1
          ? LeafletDeclarativeMapImplementation
          : OpenLayersDeclarativeMapImplementation;
      }),
      provideNavigationSettings({
        configurationFactory: () => {
          return {
            design: configService.value.design,
            url: configService.value.assistUrl ?? configService.value.navigationAppUrl ?? configService.value.url,
            navigationAppEnabled: configService.value.navigationAppEnabled,
            deploymentSettings: undefined,
            enableReservations: configService.value.enableReservations,
            companyLogoUrl: configService.value.companyLogoUrl,
            enableStepNavigation: configService.value.enableStepNavigation,
            gpsInfo: configService.value.gpsInfo,
          };
        },
      }),
      provideLocalization({
        localizations: localizations,
        translocoConfig: {
          availableLangs: configService.value.languages.filter((x) => x.activeInAssist).map((x) => x.code),
          defaultLang: configService.value.languages.find((x) => x.default).code,
          prodMode: !isDevMode(),
          reRenderOnLangChange: true,
          fallbackLang: configService.value.languages.find((x) => x.default).code,
          missingHandler: {
            useFallbackTranslation: true,
            allowEmpty: true,
            logMissingKey: true,
          },
        },
      }),
      provideCueAnalytics(
        withHeadersIntecceptor({
          apiDomains: () => {
            const configService = inject(ConfigService);
            return [configService.value.apiURL];
          },
          deviceIDHeaderName: 'deviceID',
          sessionHeaderName: 'sessionID',
        })
      ),
      importProvidersFrom(
        ServiceWorkerModule.register('/ngsw-worker.js', {
          enabled: environment.production,
          registrationStrategy: 'registerImmediately',
        })
      ),
    ],
  };
};
